import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SiteLink as Link, useLanguageQueryOnLinks } from '../components/site-link';
import { useTranslation } from 'react-i18next';
import { useUrlLocale } from '../i18n/url-locale';
import { getDoc, getDocs } from '../lib/docs';
import { searchDocArticles, type SearchSegment } from '../lib/docs-search';
import { formatDate } from '../lib/format-date';
import { usePageTitle } from '../lib/use-page-title';
import { AvailabilityBadge } from '../components/availability-badge';
import { NotFoundPage } from './not-found';

function Segments({ segments }: { segments: SearchSegment[] }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.match ? <mark key={index}>{segment.text}</mark> : <span key={index}>{segment.text}</span>,
      )}
    </>
  );
}

/**
 * The documentation index with in-browser search. The query lives in the URL
 * (`?q=`) so a search can be linked to, but it is read only after hydration:
 * the prerendered HTML is the full list, and the first client render must
 * match it.
 */
export function DocsIndexPage() {
  const { t } = useTranslation();
  const locale = useUrlLocale();
  const docs = getDocs(locale);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  usePageTitle('/docs', t('docs.title'));

  useEffect(() => {
    setQuery(searchParams.get('q') ?? '');
    // Only on mount: afterwards the input drives the URL, not the other way round.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const results = useMemo(() => (query.trim().length >= 2 ? searchDocArticles(docs, query) : null), [docs, query]);

  const onChange = (value: string) => {
    setQuery(value);
    // Other parameters (an explicit ?lang= choice) are kept.
    setSearchParams(
      (params) => {
        if (value) params.set('q', value);
        else params.delete('q');
        return params;
      },
      { replace: true },
    );
  };

  return (
    <div className="container page">
      <h1>{t('docs.title')}</h1>
      <p className="lead">{t('docs.intro')}</p>

      <form role="search" className="docs-search" action="" method="get" onSubmit={(event) => event.preventDefault()}>
        <label htmlFor="docs-q">{t('docs.searchLabel')}</label>
        <div className="docs-search-row">
          <input
            id="docs-q"
            name="q"
            type="search"
            value={query}
            placeholder={t('docs.searchPlaceholder')}
            onChange={(event) => onChange(event.target.value)}
            autoComplete="off"
          />
          <button className="button button-secondary" type="submit">
            {t('docs.searchButton')}
          </button>
        </div>
      </form>

      <p className="visually-hidden" aria-live="polite">
        {results ? t('docs.resultsCount', { count: results.length, query }) : ''}
      </p>

      {results ? (
        <section aria-label={t('docs.searchLabel')}>
          {results.length === 0 ? (
            <p>{t('docs.noResults', { query })}</p>
          ) : (
            <>
              <p aria-hidden="true">{t('docs.resultsCount', { count: results.length, query })}</p>
              <ul className="docs-list">
                {results.map((result) => (
                  <li key={result.article.slug} className="card">
                    <h2>
                      <Link to={`/docs/${result.article.slug}`}>
                        <Segments segments={result.title} />
                      </Link>
                    </h2>
                    <p>
                      <Segments segments={result.snippet} />
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
          <button className="button button-quiet" type="button" onClick={() => onChange('')}>
            {t('docs.clearSearch')}
          </button>
        </section>
      ) : (
        <ul className="docs-list" aria-label={t('docs.allArticles')}>
          {docs.map((doc) => (
            <li key={doc.slug} className="card">
              <div className="section-heading">
                <h2>
                  <Link to={`/docs/${doc.slug}`}>{doc.title}</Link>
                </h2>
                <AvailabilityBadge availability={doc.availability} />
              </div>
              <p>{doc.description}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DocArticlePage() {
  const { t } = useTranslation();
  const locale = useUrlLocale();
  const { slug = '' } = useParams();
  const doc = getDoc(slug, locale);
  const proseRef = useRef<HTMLDivElement>(null);
  useLanguageQueryOnLinks(proseRef, doc?.slug);
  usePageTitle(`/docs/${slug}`, doc?.title ?? t('notFound.title'));
  if (!doc) return <NotFoundPage />;
  const docs = getDocs(locale);
  return (
    <div className="container page docs-layout">
      <article className="prose">
        <div className="section-heading">
          <h1>{doc.title}</h1>
          <AvailabilityBadge availability={doc.availability} />
        </div>
        <p className="lead">{doc.description}</p>
        <p className="callout" role="note">
          {t('docs.foundationNote')}
        </p>
        <div ref={proseRef} dangerouslySetInnerHTML={{ __html: doc.html }} />
        <p className="meta">
          <time dateTime={doc.updated}>{t('docs.updated', { date: formatDate(doc.updated, locale) })}</time>
        </p>
        <p>
          <Link to="/docs">← {t('docs.backToDocs')}</Link>
        </p>
      </article>
      {/* After the article in the DOM and on screen, so reading, tab and visual order agree. */}
      <nav className="docs-nav" aria-label={t('docs.allArticles')}>
        <p>
          <Link to="/docs">{t('docs.title')}</Link>
        </p>
        <ul>
          {docs.map((item) => (
            <li key={item.slug}>
              <Link to={`/docs/${item.slug}`} aria-current={item.slug === doc.slug ? 'page' : undefined}>
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
